import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { useCallback, useMemo, useState } from 'react'
import TextField from 'core/components/validatedForm/TextField'
import Dropdown from 'core/elements/dropdown/Dropdown'
import { InputProps } from 'core/elements/input/Input'
import Theme from 'core/themes/model'
import { customValidator, FieldValidator } from 'core/utils/fieldValidators'

export type Unit = 'MiB' | 'GiB' | 'TiB'

export interface SizeInputWithUnit extends InputProps {
  id?: string
  label?: string
  value?: number | null
  defaultUnit?: Unit
  outputValueUnit?: Unit
  disabled?: boolean
  min?: number
  max?: number
  onChange?: (convertedValue: number | null) => void
  onUnitChange?: (unit: Unit) => void
  className?: string
  validations?: FieldValidator[]
  resetValueToZero?: boolean
  items?: { label: string; value: Unit }[]
}

const UNITS: Unit[] = ['MiB', 'GiB', 'TiB']

const UNIT_TO_MIB = {
  MiB: 1,
  GiB: 1024,
  TiB: 1024 * 1024,
}

export function convert(value: number, from: Unit, to: Unit) {
  if (!Number.isFinite(value)) return null
  const inMiB = value * UNIT_TO_MIB[from]
  return inMiB / UNIT_TO_MIB[to]
}

const defaultItems = UNITS.map((unit) => ({ label: unit, value: unit }))

const SizeInputWithUnit: React.FC<SizeInputWithUnit> = ({
  id,
  min,
  max,
  label,
  value,
  disabled,
  className,
  validations,
  defaultUnit = 'GiB',
  outputValueUnit = 'MiB',
  onChange,
  onUnitChange,
  items,
  resetValueToZero = false,
  ...rest
}) => {
  const classes = useStyles()
  const [unit, setUnit] = useState(defaultUnit)
  const [rawInput, setRawInput] = useState('')

  const isUnlimited = value === -1

  // Derived display value from props
  const displayValue = useMemo(() => {
    if (resetValueToZero) return '0'
    if (rawInput !== '') return rawInput
    if (value == null) return ''
    if (isUnlimited) return '-1'
    const converted = convert(value, outputValueUnit, unit)
    return converted === null ? '' : String(converted)
  }, [rawInput, value, isUnlimited, outputValueUnit, unit])

  const handleNumberChange = useCallback(
    (val) => {
      setRawInput(val)
      if (val === '' || val === undefined || val === null) {
        onChange && onChange(null)
        return
      }
      if (val === '-1') {
        onChange && onChange(-1)
        return
      }
      if (onChange) {
        const converted = convert(Number(val), unit, outputValueUnit)
        onChange(converted)
      }
    },
    [onChange, outputValueUnit, unit],
  )

  // The on-screen value is in the user-selected unit while the form consumes the
  // outputValueUnit-converted number — run every validator against the converted value so
  // bounds/whole-number checks stay unit-independent. Unset values and the -1 unlimited
  // sentinel pass through untouched so fieldIsUnset short-circuits keep working.
  const convertedValidations = useMemo(
    () =>
      validations?.map((validator) =>
        customValidator(
          (value, formValues, fieldPath) =>
            validator.validate(
              value === '' || value == null || value === '-1'
                ? value
                : convert(Number(value), unit, outputValueUnit),
              formValues,
              fieldPath,
            ),
          validator.errorMessage,
        ),
      ),
    [validations, unit, outputValueUnit],
  )

  const handleUnitChange = useCallback(
    (newUnit: Unit) => {
      setUnit(newUnit)
      setRawInput('')
      onUnitChange?.(newUnit)
    },
    [unit, onUnitChange],
  )

  return (
    <div className={clsx(classes.root, className)}>
      <TextField
        id={id}
        label={label}
        disabled={disabled}
        onChange={handleNumberChange}
        value={displayValue}
        type="number"
        validations={convertedValidations}
        min={min}
        max={max}
        {...rest}
        endAdornment={
          <div className={classes.unitDropdown}>
            <Dropdown
              id={`${id}-unit`}
              items={items || defaultItems}
              value={unit}
              onChange={handleUnitChange}
              disabled={disabled || isUnlimited}
              width={120}
              placeholder="Unit"
            />
          </div>
        }
      />
    </div>
  )
}

export default SizeInputWithUnit

const useStyles = makeStyles<Theme>(() => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: 8,
    alignItems: 'end',
  },
  unitDropdown: {
    '& > div': {
      '& .inputFrame': {
        borderRadius: 0,
        borderRight: 'none',
        borderTop: 'none',
        borderBottom: 'none',
        backgroundColor: 'unset',
      },
    },
  },
}))
